import * as R from "ramda";
import { getStartOfToday } from "common/date-time/calculators";
import { merge2 } from "common/merge";
import type { Context } from "common/types/context";
import type { Preferences, SchedulerSettings } from "common/types/preferences";
import { CancellablePromise } from "common/types/promises";
import {
  filterValueToFk,
  fkToFilterValue,
} from "x/scheduler2/planner/header/filter/filter-value";
import type { SchedulerData, WorkOrderData } from "x/scheduler2/types";
import { ListWithSelectItem } from "common/widgets/list-with-select/types";
import type { SchedulerSettingsType } from "common/types/scheduler-settings";
import {
  translateViewForPreferences,
  translateViewFromPreferences,
} from "./shared";

export const getSchedulerData = (context: Context): SchedulerData => {
  const allPreferences = context.preferenceService.get();
  const { defaultView, defaultSelectedDate, defaultWeekends, defaultFilter } =
    allPreferences?.scheduler ?? {};
  const translatedDefaultView = translateViewFromPreferences(defaultView);

  return {
    view: translatedDefaultView ?? "week",
    selectedDate: defaultSelectedDate ?? getStartOfToday(),
    hideWeekends: defaultWeekends ?? false,
    filterValue: fkToFilterValue(defaultFilter),
  };
};

export const getWorkOrdersData = (context: Context): WorkOrderData => {
  const allPreferences = context.preferenceService.get();
  const { defaultWOSearch, workOrdersFilter } = allPreferences?.scheduler ?? {};

  return {
    searchValue: defaultWOSearch?.value,
    filterQuery: workOrdersFilter,
  };
};

export const setSchedulerPreferences = (
  context: Context,
  newValue: SchedulerData,
) => {
  const allPreferences = context.preferenceService.get();

  const newPreferences: Preferences = {
    ...allPreferences,
    scheduler: {
      ...allPreferences.scheduler,
      defaultView: translateViewForPreferences(newValue.view),
      defaultSelectedDate: newValue.selectedDate,
      defaultWeekends: newValue.hideWeekends,
      defaultFilter: newValue.filterValue
        ? filterValueToFk(newValue.filterValue)
        : undefined,
    },
  };

  return R.equals(allPreferences, newPreferences)
    ? CancellablePromise.resolve()
    : context.preferenceService.set(newPreferences);
};

export const setDefaultPreferences = <K extends keyof SchedulerSettings>(
  context: Context,
  key: K,
  value: SchedulerSettings[K],
) => {
  const allPreferences = context.preferenceService.get();
  const newPreferences = merge2("scheduler", key, value, allPreferences);
  if (!R.equals(allPreferences, newPreferences)) {
    return context.preferenceService.set(newPreferences);
  }
  return CancellablePromise.resolve();
};

const getItemIndex = (id: string, itemOrder: ListWithSelectItem[]) => {
  return itemOrder.findIndex((item) => item.label === id) ?? -1;
};

export const schedulerGroupSortFn = (
  firstItem: any,
  secondItem: any,
  schedulerSettings: SchedulerSettingsType,
): number => {
  const { groupBy, itemOrder = [] } = schedulerSettings ?? {};
  const firstIndex = getItemIndex(firstItem[groupBy], itemOrder);
  const secondIndex = getItemIndex(secondItem[groupBy], itemOrder);

  // Handle cases where either index is -1 (not found in itemOrder)
  if (firstIndex === -1 && secondIndex === -1) return 0;
  if (firstIndex === -1) return 1;
  if (secondIndex === -1) return -1;

  return firstIndex - secondIndex;
};
